import { motion } from "framer-motion";
import { SectionContainer } from "@/components/shared/SectionContainer";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { SkillGroup } from "@portfolio/types";

export function SkillsGrid() {
  const { data } = useQuery({
    queryKey: ["skills"],
    queryFn: () => api.get<SkillGroup[]>("/api/skills"),
  });

  if (!data || data.length === 0) {
    return (
      <SectionContainer className="border-t border-border/50">
        <SectionHeading eyebrow="Stack" title="Skills" />
        <p className="mt-6 text-muted-foreground">
          Skills are loading or have not been added yet.
        </p>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer className="border-t border-border/50">
      <SectionHeading eyebrow="Stack" title="Skills" />
      <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {data.map((group) => (
          <div
            key={group.category}
            className="rounded-xl border border-border bg-card/60 p-5 backdrop-blur"
          >
            <div className="text-xs uppercase tracking-[0.15em] text-primary">
              {group.category}
            </div>
            <div className="mt-4 space-y-3">
              {group.skills.map((s) => (
                <div key={s.id} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-foreground">{s.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {s.proficiency}/5
                    </span>
                  </div>
                  <ProficiencyBar value={s.proficiency} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </SectionContainer>
  );
}

function ProficiencyBar({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(5, value)) / 5;
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
      <motion.div
        initial={{ width: 0 }}
        whileInView={{ width: `${pct * 100}%` }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="h-full rounded-full bg-gradient-to-r from-primary via-amber-300 to-amber-500"
      />
    </div>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  description,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
}) {
  return (
    <div className="max-w-2xl">
      {eyebrow && (
        <div className="text-xs uppercase tracking-[0.2em] text-primary">{eyebrow}</div>
      )}
      <h2 className="mt-2 font-serif text-3xl sm:text-4xl">{title}</h2>
      {description && (
        <p className="mt-3 text-muted-foreground">{description}</p>
      )}
    </div>
  );
}
